import * as THREE from 'three';
import { drawnMaps, grey, seeded, shadeOf } from './kit.js';

/**
 * The bar along the back wall of the club, and going through it.
 *
 * To the collision it is one hard `box`. Here it is built as a row of
 * `segments` — each a front panel and a counter top in two halves — so that
 * the part a body arrives at is the part that gives. Bottles stand on the
 * counter and on the shelves behind it, one instanced mesh each.
 *
 * Like the garage's cars the damage is **state first**: `bar.damage.segments`
 * says which section is intact, cracked or smashed, and how many bottles are
 * still standing, and the unit suite reads that in `node`. The picture follows:
 *
 *   a hit       the section's panel is knocked askew and the bottles near the
 *               impact go over. A second hit on a cracked section smashes it.
 *   a finish    straight through: the panel is gone, the counter top folds
 *               into a V, the bottles near it are flung, splinters land on the
 *               floor — and `hit` answers with a resting place **inside** the
 *               bar's footprint, which the host eases the body into once its
 *               rebound off the front has played out.
 *
 * Put right by `reset()` at every intro.
 */

export const SECTION = Object.freeze(['intact', 'cracked', 'smashed']);

export function createBar(kit, surface, { palette = {}, config = {}, damage: rules = {} } = {}) {
  const { center, halfX, halfZ } = surface;
  const height = surface.height ?? 1.08;
  const count = config.segments ?? 8;
  const width = (halfX * 2) / count;
  const front = center.z + halfZ;
  const back = center.z - halfZ;
  const topThickness = config.top ?? 0.06;
  const tone = (key, fallback) => new THREE.Color(palette[key] ?? fallback);
  const random = seeded(29);

  const group = new THREE.Group();
  group.name = 'bar';

  // Wood that reads as wood under the back-bar lamp (step 98): a grain down
  // each panel, and a varnished top worn dull where elbows go and ringed where
  // glasses stood — both in the roughness, so the sheen breaks up.
  const grain = drawnMaps(128, 256, (c, r, w, h, rnd) => {
    c.fillStyle = shadeOf(tone('barWood', '#4a2c1c'), 1);
    c.fillRect(0, 0, w, h);
    r.fillStyle = grey(0.55);
    r.fillRect(0, 0, w, h);
    for (let i = 0; i < 70; i++) {
      const x = rnd() * w;
      const width = 0.5 + rnd() * 2.5;
      const dark = rnd() < 0.6;
      c.strokeStyle = dark ? `rgba(20,8,2,${0.15 + rnd() * 0.3})` : `rgba(255,200,150,${0.04 + rnd() * 0.08})`;
      c.lineWidth = width;
      c.beginPath();
      const sway = 2 + rnd() * 6;
      const phase = rnd() * 6;
      for (let y = 0; y <= h; y += 8) c.lineTo(x + Math.sin(y / 40 + phase) * sway, y);
      c.stroke();
      r.strokeStyle = grey(dark ? 0.7 : 0.45);
      r.lineWidth = width;
      r.stroke();
    }
  }, { seed: 31 });
  const worn = drawnMaps(256, 128, (c, r, w, h, rnd) => {
    c.fillStyle = shadeOf(tone('barTop', '#2b1a12'), 1);
    c.fillRect(0, 0, w, h);
    r.fillStyle = grey(0.22);
    r.fillRect(0, 0, w, h);
    // Elbows along the customer's edge.
    const elbows = r.createLinearGradient(0, 0, 0, h);
    elbows.addColorStop(0, 'rgba(255,255,255,0)');
    elbows.addColorStop(1, 'rgba(255,255,255,0.35)');
    r.fillStyle = elbows;
    r.fillRect(0, 0, w, h);
    for (let i = 0; i < 9; i++) {
      const x = rnd() * w;
      const y = h * (0.3 + rnd() * 0.6);
      const radius = 6 + rnd() * 5;
      c.strokeStyle = 'rgba(0,0,0,0.25)';
      c.lineWidth = 1.5;
      c.beginPath();
      c.arc(x, y, radius, 0, Math.PI * 2);
      c.stroke();
      r.strokeStyle = grey(0.75);
      r.lineWidth = 2;
      r.beginPath();
      r.arc(x, y, radius, 0, Math.PI * 2);
      r.stroke();
    }
  }, { seed: 37 });
  for (const t of [grain.map, grain.roughnessMap, worn.map, worn.roughnessMap]) if (t) kit.keep(t);
  const wood = kit.material({
    color: grain.map ? 0xffffff : tone('barWood', '#4a2c1c'),
    map: grain.map,
    roughnessMap: grain.roughnessMap,
    roughness: grain.roughnessMap ? 1 : 0.62,
    metalness: 0.05,
  });
  const top = kit.material({
    color: worn.map ? 0xffffff : tone('barTop', '#2b1a12'),
    map: worn.map,
    roughnessMap: worn.roughnessMap,
    roughness: worn.roughnessMap ? 1 : 0.25,
    metalness: 0.1,
  });
  const inside = kit.material({ color: tone('barInside', '#120d0b'), roughness: 1 });
  const brass = kit.material({ color: tone('brass', '#b08d4a'), roughness: 0.3, metalness: 0.85 });
  const at = (geometry, mat, options) => kit.place(geometry, mat, { parent: group, ...options });

  // The bartender's side and the dark inside, which is what shows once the
  // front is gone.
  at(new THREE.BoxGeometry(halfX * 2, height - topThickness, 0.04), inside, { x: center.x, y: (height - topThickness) / 2, z: back + 0.02, name: 'bar-back' });
  at(new THREE.BoxGeometry(halfX * 2, 0.02, halfZ * 2), inside, { x: center.x, y: 0.01, z: center.z, name: 'bar-floor', casts: false });
  // A brass foot rail along the front, the one thing every bar has.
  at(new THREE.CylinderGeometry(0.025, 0.025, halfX * 2, 10), brass, { x: center.x, y: 0.18, z: front + 0.12, rotZ: Math.PI / 2, name: 'bar-rail' });

  const segments = [];
  for (let i = 0; i < count; i++) {
    const x = center.x - halfX + width * (i + 0.5);
    const panel = at(new THREE.BoxGeometry(width - 0.01, height - topThickness, 0.05), wood, {
      x,
      y: (height - topThickness) / 2,
      z: front - 0.025,
      name: `bar-panel-${i}`,
    });
    // The top in two halves, each hung from its outer end, so a finish can
    // fold the section down into a V rather than just delete it.
    const halves = [-1, 1].map((side) => {
      const pivot = new THREE.Group();
      pivot.position.set(x + (side * width) / 2, height - topThickness / 2, center.z);
      group.add(pivot);
      at(new THREE.BoxGeometry(width / 2, topThickness, halfZ * 2 + 0.08), top, {
        x: (-side * width) / 4,
        z: 0.02,
        name: `bar-top-${i}`,
        parent: pivot,
      });
      return { pivot, side };
    });
    segments.push({ x, panel, halves });
  }

  // --- the bottles -----------------------------------------------------------
  const bottleGeometry = kit.keep(new THREE.CylinderGeometry(0.035, 0.04, 0.3, 8));
  const glass = kit.material({ color: tone('bottle', '#3f6b4a'), roughness: 0.15, metalness: 0.1, transparent: true, opacity: 0.85 });
  const onCounter = config.bottles ?? 30;
  const counter = new THREE.InstancedMesh(bottleGeometry, glass, onCounter);
  counter.name = 'bar-bottles';
  const bottles = [];
  for (let i = 0; i < onCounter; i++) {
    bottles.push({
      x: center.x - halfX + 0.1 + random() * (halfX * 2 - 0.2),
      z: center.z + (random() - 0.5) * halfZ * 1.2,
      state: 'standing',
      spin: random() * Math.PI * 2,
    });
  }
  group.add(counter);

  const shelf = config.shelf;
  if (shelf) {
    const board = kit.material({ color: tone('shelf', '#2a1d16'), roughness: 0.8 });
    const behind = new THREE.InstancedMesh(bottleGeometry, glass, shelf.rows.length * shelf.bottles);
    behind.name = 'bar-shelf-bottles';
    const m = new THREE.Matrix4();
    let k = 0;
    for (const y of shelf.rows) {
      at(new THREE.BoxGeometry(halfX * 2, 0.03, 0.22), board, { x: center.x, y: y - 0.015, z: shelf.z, name: 'bar-shelf' });
      for (let j = 0; j < shelf.bottles; j++) {
        m.makeTranslation(center.x - halfX + 0.15 + (j / (shelf.bottles - 1)) * (halfX * 2 - 0.3), y + 0.15, shelf.z);
        behind.setMatrixAt(k++, m);
      }
    }
    group.add(behind);
  }

  // Splinters, preallocated, shown on a finish.
  const debrisCount = rules.debris ?? 40;
  const debris = new THREE.InstancedMesh(kit.keep(new THREE.BoxGeometry(0.28, 0.025, 0.07)), wood, debrisCount);
  debris.name = 'bar-debris';
  debris.count = 0;
  group.add(debris);

  const damage = {
    segments: segments.map(() => 'intact'),
    standing: onCounter,
    hits: 0,
  };

  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const one = new THREE.Vector3(1, 1, 1);
  function drawBottles() {
    let shown = 0;
    for (const bottle of bottles) {
      if (bottle.state === 'flung') continue;
      if (bottle.state === 'down') {
        e.set(Math.PI / 2, bottle.spin, 0, 'YXZ');
        q.setFromEuler(e);
        m.compose(new THREE.Vector3(bottle.x, height + 0.04, bottle.z), q, one);
      } else {
        m.makeTranslation(bottle.x, height + 0.15, bottle.z);
      }
      counter.setMatrixAt(shown++, m);
    }
    counter.count = shown;
    counter.instanceMatrix.needsUpdate = true;
  }
  drawBottles();

  function drawSegment(index) {
    const segment = segments[index];
    const state = damage.segments[index];
    segment.panel.visible = state !== 'smashed';
    segment.panel.rotation.set(state === 'cracked' ? -0.12 : 0, 0, state === 'cracked' ? 0.05 : 0);
    segment.panel.position.z = front - 0.025 + (state === 'cracked' ? -0.03 : 0);
    const fold = state === 'smashed' ? rules.fold ?? 0.55 : state === 'cracked' ? 0.04 : 0;
    for (const { pivot, side } of segment.halves) pivot.rotation.z = side * fold;
  }

  function splinter(x) {
    const start = debris.count;
    const added = Math.min(14, debrisCount - start);
    for (let i = 0; i < added; i++) {
      e.set(random() * 0.4, random() * Math.PI * 2, random() * 0.4);
      q.setFromEuler(e);
      const z = front + 0.1 + random() * 0.9 - (random() < 0.35 ? halfZ * 1.6 : 0);
      m.compose(new THREE.Vector3(x + (random() - 0.5) * 1.4, 0.02 + random() * 0.04, z), q, one);
      debris.setMatrixAt(start + i, m);
    }
    debris.count = start + added;
    debris.instanceMatrix.needsUpdate = true;
  }

  const sectionAt = (x) => Math.max(0, Math.min(count - 1, Math.floor((x - (center.x - halfX)) / width)));

  return {
    id: surface.id,
    object3D: group,
    damage,
    segments,

    /**
     * A body arrived at the bar. Returns where a finished body comes to rest,
     * or null.
     *
     * Only the front can be reached from the floor; a blow from any other side
     * still breaks the section it lands on, because nothing else would make
     * sense of it.
     */
    hit({ at = null, knockout = false } = {}) {
      const x = at ? at.x : center.x;
      const index = sectionAt(x);
      damage.hits += 1;
      const was = damage.segments[index];
      damage.segments[index] = knockout || was !== 'intact' ? 'smashed' : 'cracked';
      // A finish takes the sections either side with it, half way.
      if (knockout) {
        for (const near of [index - 1, index + 1]) {
          if (near >= 0 && near < count && damage.segments[near] === 'intact') damage.segments[near] = 'cracked';
        }
      }
      for (let i = Math.max(0, index - 1); i <= Math.min(count - 1, index + 1); i++) drawSegment(i);

      const reach = knockout ? rules.fling ?? 1.2 : rules.topple ?? 0.8;
      for (const bottle of bottles) {
        if (bottle.state === 'flung' || Math.abs(bottle.x - x) > reach) continue;
        bottle.state = knockout ? 'flung' : 'down';
      }
      damage.standing = bottles.filter((bottle) => bottle.state === 'standing').length;
      drawBottles();

      if (damage.segments[index] !== 'smashed') return null;
      splinter(segments[index].x);
      // In it, not in front of it: the middle of the section that gave, a
      // little in from the front.
      return knockout ? { rest: { x: segments[index].x, z: center.z + halfZ * 0.15 }, arc: 0.25 } : null;
    },

    reset() {
      damage.segments.fill('intact');
      damage.hits = 0;
      for (const bottle of bottles) bottle.state = 'standing';
      damage.standing = bottles.length;
      for (let i = 0; i < count; i++) drawSegment(i);
      drawBottles();
      debris.count = 0;
    },
  };
}
